import { supabase } from '@/integrations/supabase/client';

// ============= Types =============
export type MediaBucket = 'portfolio' | 'logos';

export interface UploadResult {
  path: string;
  url: string;
}

export interface MediaFile {
  id: string;
  name: string; // For compatibility with MediaPicker
  filename: string;
  original_filename: string | null;
  path: string;
  bucket: string;
  url: string;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string;
}

// ============= Folder Configuration =============
const BUCKET_FOLDERS: Record<string, string> = {
  portfolio: '',
  logos: '',
  services: 'services',
  banners: 'banners',
};

// ============= Client-side validation (pre-upload) =============
const ALLOWED_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

export const validateImageClient = (file: File): { isValid: boolean; error?: string } => {
  if (!ALLOWED_FORMATS.includes(file.type)) {
    return { isValid: false, error: 'Invalid file format. Only JPG, PNG, and WEBP are allowed.' };
  }
  if (file.size > MAX_FILE_SIZE) {
    return { isValid: false, error: 'File size exceeds 2MB limit.' };
  }
  return { isValid: true };
};

// Video validation and upload helper
const ALLOWED_VIDEO_FORMATS = ['video/mp4', 'video/webm', 'video/quicktime'];
const MAX_VIDEO_SIZE = 250 * 1024 * 1024; // 250MB

export const validateVideoClient = (file: File): { isValid: boolean; error?: string } => {
  if (!ALLOWED_VIDEO_FORMATS.includes(file.type)) {
    return { isValid: false, error: 'Invalid file format. Only MP4, MOV, and WEBM are allowed.' };
  }
  if (file.size > MAX_VIDEO_SIZE) {
    return { isValid: false, error: 'Video file size exceeds 250MB limit.' };
  }
  return { isValid: true };
};

export const uploadVideoSecure = async (
  file: File,
  bucketType: MediaBucket,
  folder?: string
): Promise<UploadResult> => {
  const validation = validateVideoClient(file);
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  // Use same backend secure upload function
  const actualBucket = bucketType === 'portfolio' || bucketType === 'logos' ? bucketType : 'portfolio';
  const actualFolder = folder || BUCKET_FOLDERS[bucketType] || '';

  const formData = new FormData();
  formData.append('file', file);
  formData.append('bucket', actualBucket);
  formData.append('folder', actualFolder);
  formData.append('mediaType', 'video');

  // Include the current user's access token so the edge function can authorize the request
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const { data, error } = await supabase.functions.invoke('secure-media-upload', {
    body: formData as unknown,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  } as unknown);

  // Surface server-side errors if present
  if (error) {
    const msg = error?.message || JSON.stringify(error);
    throw new Error(msg || 'Upload failed');
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  if (!data?.url || !data?.path) {
    throw new Error('Upload failed: Invalid response from server');
  }

  return { path: data.path, url: data.url };
};
// ============= Secure Upload via Backend Function =============
export const uploadMediaSecure = async (
  file: File,
  bucketType: MediaBucket,
  folder?: string
): Promise<UploadResult> => {
  const validation = validateImageClient(file);
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  // Determine bucket and folder
  const actualBucket = bucketType === 'portfolio' || bucketType === 'logos' ? bucketType : 'portfolio';
  const actualFolder = folder || BUCKET_FOLDERS[bucketType] || '';

  const formData = new FormData();
  formData.append('file', file);
  formData.append('bucket', actualBucket);
  formData.append('folder', actualFolder);
  formData.append('mediaType', 'image');

  // Include the current user's access token so the edge function can authorize the request
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const { data, error } = await supabase.functions.invoke('secure-media-upload', {
    body: formData as unknown,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  } as unknown);

  if (error) {
    const msg = error?.message || JSON.stringify(error);
    throw new Error(msg || 'Upload failed');
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  if (!data?.url || !data?.path) {
    throw new Error('Upload failed: Invalid response from server');
  }

  return { path: data.path, url: data.url };
};

// ============= Delete via Backend Function =============
export const deleteMediaSecure = async (path: string, bucket: MediaBucket): Promise<void> => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const { data, error } = await supabase.functions.invoke('secure-media-upload', {
    body: { path, bucket },
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  } as unknown);

  if (error) {
    throw new Error(error.message || 'Delete failed');
  }

  if (data?.error) {
    throw new Error(data.error);
  }
};

// ============= List Media from Library =============
export const listMediaFromLibrary = async (bucket?: MediaBucket): Promise<MediaFile[]> => {
  let query = supabase
    .from('media_library')
    .select('*')
    .order('created_at', { ascending: false });

  if (bucket) {
    query = query.eq('bucket', bucket);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Failed to fetch media: ${error.message}`);
  }

  // Generate URLs for each item
  return (data || []).map((item) => {
    const it = item as Record<string, unknown>;
    const path = String(it.path ?? '');
    const bucket = String(it.bucket ?? '') as MediaBucket;
    return {
      id: String(it.id ?? ''),
      name: String(it.filename ?? ''),
      filename: String(it.filename ?? ''),
      original_filename: it.original_filename ? String(it.original_filename) : null,
      path,
      bucket,
      url: getMediaUrl(path, bucket),
      mime_type: it.mime_type ? String(it.mime_type) : null,
      size_bytes: typeof it.size_bytes === 'number' ? Number(it.size_bytes) : null,
      created_at: String(it.created_at ?? ''),
    } as MediaFile;
  });
};

// ============= Get Public URL =============
export const getMediaUrl = (path: string, bucket: MediaBucket): string => {
  if (!path) return '';
  
  // If already a full URL, return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
};

// ============= Check if path is relative =============
export const isRelativePath = (value: string): boolean => {
  if (!value) return false;
  return !value.startsWith('http://') && !value.startsWith('https://');
};
